import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';
type ColorScheme = 'blue' | 'green' | 'purple' | 'orange';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  colorScheme: ColorScheme;
  setTheme: (theme: Theme) => void;
  setColorScheme: (colorScheme: ColorScheme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>('light');
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light');
  const [colorScheme, setColorScheme] = useState<ColorScheme>('blue');

  // Hardcode theme to light, ignore local storage related to theme
  useEffect(() => {
    // Only check color scheme
    const savedColorScheme = localStorage.getItem('colorScheme') as ColorScheme;
    if (savedColorScheme && ['blue', 'green', 'purple', 'orange'].includes(savedColorScheme)) {
      setColorScheme(savedColorScheme);
    }

    // Enforce light mode class
    const root = document.documentElement;
    root.classList.remove('dark'); // Ensure dark class is removed
  }, []);

  // Update color scheme only
  useEffect(() => {
    localStorage.setItem('colorScheme', colorScheme);

    const root = document.documentElement;
    // Remove all color scheme classes
    root.classList.remove('color-blue', 'color-green', 'color-purple', 'color-orange');
    // Add current color scheme class
    root.classList.add(`color-${colorScheme}`);
  }, [colorScheme]);

  const handleSetTheme = (newTheme: Theme) => {
    // Do nothing essentially, or allow it but useEffect ensures it's always light rendering-wise?
    // User requested "zorunlu olarak açık tema". So we just don't change it.
    console.log("Theme change requested but forced to light mode.");
    setTheme('light');
  };

  const handleSetColorScheme = (newColorScheme: ColorScheme) => {
    setColorScheme(newColorScheme);
  };

  return (
    <ThemeContext.Provider value={{ theme: 'light', resolvedTheme: 'light', colorScheme, setTheme: handleSetTheme, setColorScheme: handleSetColorScheme }}>
      {children}
    </ThemeContext.Provider>
  );
};